/* eslint prettier/prettier: ["error", { printWidth: 1000 }] */
import { ProductsTab } from '../models/ProductsTab';
import { productResources } from '../resources/builders/productResources';
import { IResource } from '../resources/resource';
import { userDownloadsResources } from '../resources/builders/userDownloadsResources';
import { organizationResources } from '../resources/builders/organizationResources';
import { productReleaseResources } from '../resources/builders/productReleaseResources';
import { organizationProductResources } from '../resources/builders/organizationProductResources';
import { userResources } from '../resources/builders/userResources';
import { roleResources } from '../resources/builders/roleResources';
import { licenseResources } from '../resources/builders/licenseResources';
import { cloudLiveResources } from '../resources/builders/cloudLiveResources';
import { scheduledResources } from '../resources/builders/scheduledResources';
import { PendingJobType } from '../models/CronJob';
import { eventResources } from '../resources/builders/eventResources';
import { overviewResources } from '../resources/builders/overviewResources';
import { documentationResources } from '../resources/builders/documentationResources';
import { activityResources } from '../resources/builders/activityResources';
import { useContext, useMemo } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { ArgsType } from '../types/ArgsType';
import { IBasicOrganization } from '../resources/models/IOrganization';
import { AuthContext } from '../contexts/AuthContext';
import { Strictify } from '../types/Strictify';
import { zendeskResources } from '../resources/builders/zendeskResources';

export const authenticatedRoot = '/panel';

const encode = (key: string, value?: string) => {
  return value ? encodeURIComponent(value) : `:${key}`;
};

export const AuthenticatedRoutes = {
  ChooseOrganization: (): string => `${authenticatedRoot}/choose-organization`,

  ViewAccount: (): string => `${authenticatedRoot}/account`,

  NoOrganization: (): string => `${authenticatedRoot}/no-organization`,

  // Organization
  CurrentOrganizationOverview: (): string => `${authenticatedRoot}/organization/overview`,
  CurrentOrganizationOverviewProduct: (product?: string): string => `${authenticatedRoot}/organization/overview/${product ?? ':product'}`,
  CurrentOrganizationUsers: (): string => `${authenticatedRoot}/organization/users`,
  CurrentOrganizationDetails: (): string => `${authenticatedRoot}/organization/details`,
  CurrentOrganizationProducts: (): string => `${authenticatedRoot}/organization/products`,
  CurrentOrganizationProduct: (customer_product_id?: number): string => `${authenticatedRoot}/organization/products/${customer_product_id ?? ':customer_product_id'}`,
  CurrentOrganizationDealHistory: (organization_id?: string, product_id?: number): string => `${authenticatedRoot}/organization/${organization_id ?? ':organization_id'}/products/${product_id ?? ':product_id'}/deal-history`,
  CurrentOrganizationInvites: (): string => `${authenticatedRoot}/organization/invites`,

  CurrentOrganizationCloudLive: (): string => `${authenticatedRoot}/organization/cloud-live`,

  Licenses: (): string => `${authenticatedRoot}/licenses`,
  AllLicenses: (): string => `${authenticatedRoot}/all-licenses`,

  Support: (): string => `${authenticatedRoot}/support`,

  // Global
  GlobalOverview: (): string => `${authenticatedRoot}/overview`,
  GlobalOverviewProduct: (product?: string): string => `${authenticatedRoot}/overview/${product ?? ':product'}`,

  AdminProducts: (): string => `${authenticatedRoot}/products`,
  AdminProductsTab: (tab?: ProductsTab): string => `${authenticatedRoot}/products/${tab ?? ':tab'}`,

  Releases: (): string => `${authenticatedRoot}/releases`,
  ReleasesProduct: (productId?: string): string => `${authenticatedRoot}/releases/${productId ?? ':productId'}`,
  ReleasesProductVersion: (productId?: string, version?: string): string => `${authenticatedRoot}/releases/${productId ?? ':productId'}/version/${version ?? ':version'}`,
  Organizations: (): string => `${authenticatedRoot}/organizations`,

  OrganizationDetails: (organization_name?: string): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}`,
  // `${authenticatedRoot}/organizations/${organization_name ?? ':organization_name'}`,
  OrganizationDetailsProducts: (organization_name?: string): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/products`,
  OrganizationDetailsUsers: (organization_name?: string): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/users`,
  OrganizationDetailsInvites: (organization_name?: string): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/invites`,
  OrganizationDetailsTickets: (organization_name?: string): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/tickets`,
  OrganizationDetailsLive: (organization_name?: string): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/live`,
  OrganizationProduct: (organization_name?: string, customer_product_id?: number): string => `${authenticatedRoot}/organizations/${encode('organization_name', organization_name)}/products/${customer_product_id ?? ':customer_product_id'}`,

  AllUsers: (): string => `${authenticatedRoot}/users`,
  User: (user_id?: string): string => `${authenticatedRoot}/users/${user_id ?? ':user_id'}`,
  UserRoles: (user_id?: string): string => `${authenticatedRoot}/users/${user_id ?? ':user_id'}/roles`,
  UserOrganization: (organization_name?: string, user_id?: string): string => `${authenticatedRoot}/users/${user_id ?? ':user_id'}/organization/${encode('organization_name', organization_name)}`,
  UserDownloads: (): string => `${authenticatedRoot}/downloads`,
  UserDownloadsProduct: (product?: number): string => `${authenticatedRoot}/downloads/${product ?? ':product'}`,
  UserDownloadsProductLens: (product?: number): string => `${authenticatedRoot}/downloads/${product ?? ':product'}/lens`,
  UserDownloadsProductVersion: (product?: number, version?: string): string => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}`,
  UserDownloadsProductExamples: (product?: number, version?: string): string => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/examples`,
  UserDownloadsProductContent: (product?: number, version?: string): string => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/content`,
  UserDownloadsProductTools: (product?: number, version?: string): string => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/tools`,
  UserDownloadsProductInstaller: (product?: number, version?: string, installer?: string): string => `${authenticatedRoot}/downloads/${product ?? ':product'}/${version ?? ':version'}/${installer ?? ':installer'}`,
  Documentation: (): string => `${authenticatedRoot}/docs`,
  DocumentationProduct: (product?: number): string => `${authenticatedRoot}/docs/${product ?? ':product'}`,
  Admins: (): string => `${authenticatedRoot}/admins`,

  CloudLive: (): string => `${authenticatedRoot}/cloud-live`,

  Jobs: (): string => `${authenticatedRoot}/jobs`,
  JobsType: (job_type?: PendingJobType): string => `${authenticatedRoot}/jobs/${job_type ?? ':job_type'}`,

  Events: (): string => `${authenticatedRoot}/api-events`,

  Activity: (): string => `${authenticatedRoot}/activity`,
  ActivityItem: (ip?: string): string => `${authenticatedRoot}/activity/${ip ?? ':ip'}`,

  Unverified: (): string => `${authenticatedRoot}/unverified`,
};

type OptArr<T> = T | T[];

type RouteResources = {
  [RouteName in keyof typeof AuthenticatedRoutes]: (...args: Strictify<ArgsType<typeof AuthenticatedRoutes[RouteName]>>) => OptArr<IResource<any, any> | null> | boolean;
};

const anyOrg = {
  id: 'any',
  name: 'any',
} as IBasicOrganization;

export const useRouteResources = (): RouteResources => {
  const { organization } = useContext(OrganizationContext);
  const { identity } = useContext(AuthContext);
  return useMemo<RouteResources>(
    () => ({
      ChooseOrganization: () => true,
      ViewAccount: () => true,
      NoOrganization: () => true,
      CurrentOrganizationOverview: () => (organization ? overviewResources.getProductsList(organization) : null),
      CurrentOrganizationOverviewProduct: () => (organization ? overviewResources.getProductsList(organization) : null),
      CurrentOrganizationUsers: () => (organization ? organizationResources.listOrganizationUsers(organization.name) : null),
      CurrentOrganizationDetails: () => (organization ? organizationResources.updateCurrentOrganizationByName(organization.name) : null),
      CurrentOrganizationProducts: () => (organization ? organizationProductResources.getOrganizationProducts(organization) : null),
      CurrentOrganizationProduct: (customer_product_id) => (organization ? organizationProductResources.getOrganizationProductLicenses(organization, customer_product_id) : null),
      CurrentOrganizationDealHistory: (organization_id, product_id) => (organization ? organizationProductResources.getDealHistory(organization, product_id) : null),
      CurrentOrganizationInvites: () => (organization ? organizationResources.getInvitations(organization.name) : null),
      CurrentOrganizationCloudLive: () => (organization ? cloudLiveResources.getOrganizationLiveStacks(organization, 'any') : null),
      GlobalOverview: () => overviewResources.getSummaryExpirations(),
      GlobalOverviewProduct: () => overviewResources.getSummaryExpirations(),
      AdminProducts: () => productResources.getProducts(),
      AdminProductsTab: () => productResources.getProducts(),
      Releases: () => productReleaseResources.getProductReleases('any'),
      ReleasesProduct: (product) => productReleaseResources.getProductReleases(product),
      ReleasesProductVersion: (product, version) => productReleaseResources.updateProductReleaseVersion(product, version),
      Organizations: () => organizationResources.getOrganizations(),
      OrganizationDetails: (organization_name) => organizationResources.getOrganizationByName(organization_name),
      OrganizationDetailsProducts: () => organizationProductResources.getOrganizationProducts(anyOrg),
      OrganizationDetailsUsers: (organization_name) => organizationResources.listOrganizationUsers(organization_name),
      OrganizationDetailsInvites: (organization_name) => organizationResources.getInvitations(organization_name),
      OrganizationDetailsTickets: () => false,
      OrganizationDetailsLive: () => cloudLiveResources.getOrganizationLiveStacks(anyOrg, 'any'),
      OrganizationProduct: (org, product) => organizationProductResources.getOrganizationProductLicenses(anyOrg, product),
      Licenses: () => (organization && identity ? licenseResources.getUserLicenses(organization, identity.userId) : null),
      AllLicenses: () => (organization ? licenseResources.getOrganizationLicenses(organization) : null),
      Support: () => (organization && identity ? zendeskResources.getUserTickets(identity.userId, 'any') : null),
      AllUsers: () => userResources.getUsers(),
      User: (user_id) => userResources.getUser(user_id),
      UserRoles: (user_id) => [userResources.getUserInfo(user_id), roleResources.listRoles(), organizationResources.getOrganizations()],
      UserOrganization: (organization_name, user_id) => organizationResources.getUser(organization_name, user_id),
      UserDownloads: () => (organization ? userDownloadsResources.listDownloads(organization) : null),
      UserDownloadsProduct: () => (organization ? userDownloadsResources.listDownloads(organization) : null),
      UserDownloadsProductVersion: () => (organization ? userDownloadsResources.getEngineContent(organization) : null),
      UserDownloadsProductExamples: () => (organization ? userDownloadsResources.getExamples(organization) : null),
      UserDownloadsProductContent: () => (organization ? userDownloadsResources.getEngineContent(organization) : null),
      UserDownloadsProductTools: () => (organization ? userDownloadsResources.getTools(organization) : null),
      UserDownloadsProductInstaller: () => userDownloadsResources.getFile(),
      UserDownloadsProductLens: () => (organization ? userDownloadsResources.getLens(organization) : null),
      Documentation: () => (organization ? documentationResources.getDocumentation(organization) : null),
      DocumentationProduct: () => (organization ? documentationResources.getDocumentation(organization) : null),
      Admins: () => userResources.getAdmins(),
      CloudLive: () => cloudLiveResources.getAllLiveStacks('any'),
      Jobs: () => scheduledResources.getAllJobs(),
      JobsType: () => scheduledResources.getAllJobs(),
      Events: () => eventResources.getApiEvents(),
      Activity: () => activityResources.getActivity(),
      ActivityItem: () => activityResources.getIpActivity(),
      Unverified: () => true,
    }),
    [organization, identity]
  );
};
